import { useEffect, useRef, useState } from "react";
import { alpha } from '@mui/material/styles';
import {
  Typography, Box,
  Button, useTheme, useMediaQuery, TextField, Divider, IconButton, Collapse,
  Chip, Stack, Switch, FormControlLabel, ToggleButtonGroup, ToggleButton, Dialog, DialogContent, Tooltip
} from '@mui/material';
import { Delete, LocalOffer as LocalOfferIcon, ShoppingCartCheckout as ShoppingCartCheckoutIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { ccyFormat } from "../../helpers/helper";
import { Modal, RadioGroup, Deposit } from "..";
import { CHANGE, TYPE_SALE } from "../../helpers/enums";
import { BRANDING } from "../../config/branding";

const CarritoView = ({
  rows,
  onRemoveItem,
  onCancel,
  onSell,
  users,
  width = '100%',
  onEditItem,
  onChangePrice,
  withDescription = true,
  nameFieldRef,
  initialName = '',
  existingItems = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [name, setName] = useState(initialName);
  const [selectedUser, setSelectedUser] = useState("");
  const [data, setData] = useState(rows);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPriceTypes, setSelectedPriceTypes] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [mobileSection, setMobileSection] = useState('products');
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', name: '' });
  const [pago, setPago] = useState(0);
  const lastEnterRef = useRef(0);

  const totalSale = data.reduce((acc, item) => acc + (item.prices_sold?.sale_price * item.quantity_sold), 0);
  const existingTotal = existingItems.reduce(
    (acc, item) => acc + ((item.prices_sold?.sale_price || 0) * (item.quantity_sold || 0)),
    0
  );
  const totalUnits = data.reduce((acc, item) => acc + (item.quantity_sold || 0), 0);
  const balance = pago - totalSale;
  const changeAmount = Math.max(0, balance);
  const pendingAmount = Math.max(0, -balance);

  useEffect(() => {
    setData(rows);
    if (rows && rows.length === 0) {
      onCancel && onCancel();
    }
  }, [rows, onCancel]);

  useEffect(() => {
    if (initialName) {
      setName(initialName);
    }
  }, [initialName]);

  useEffect(() => {
    if (!isMobile) return;
    if (!data?.length) {
      setMobileSection('products');
    }
  }, [isMobile, data]);

  // Actualizar datos con variantes seleccionadas cuando cambian las selecciones
  useEffect(() => {
    setData(prevData => 
      prevData.map(item => {
        const variantIndex = selectedVariants[item.article_id];
        const sizeIndex = selectedVariants[`${item.article_id}_size`];
        
        if (variantIndex !== undefined && item.type_detail && item.type_detail[variantIndex]) {
          const selectedVariant = item.type_detail[variantIndex];
          return {
            ...item,
            color_selected: selectedVariant.color || '',
            size_selected: sizeIndex !== undefined && selectedVariant.sizes && selectedVariant.sizes[sizeIndex] 
              ? selectedVariant.sizes[sizeIndex] 
              : ''
          };
        }
        return item;
      })
    );
  }, [selectedVariants]);

  useEffect(() => {
    const DOUBLE_ENTER_MS = 400;
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (openModal || data.length === 0) return;

      e.preventDefault();
      const now = Date.now();
      const gap = now - lastEnterRef.current;
      lastEnterRef.current = now;

      if (gap < DOUBLE_ENTER_MS) {
        // Doble Enter → Entregar directo
        onSell && onSell({ name, delivery: true, vendor: selectedUser });
      } else {
        // Simple Enter → abrir modal de seña (igual que botón Reservar)
        setTimeout(() => {
          if (Date.now() - lastEnterRef.current >= DOUBLE_ENTER_MS) {
            setOpenModal(true);
          }
        }, DOUBLE_ENTER_MS);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data, name, selectedUser, onSell, openModal]);

  const handleChangePrice = (type, value, item) => {
    setSelectedPriceTypes(prev => ({
      ...prev,
      [item.article_id]: type
    }));
    onChangePrice && onChangePrice(type, value, item);
  };

  return (
    <Box
      sx={{
        width: '100%',
        padding: 2,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      {existingItems.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
            backgroundColor: alpha(theme.palette.info.main, 0.06),
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center' gap={1} mb={1}>
            <Box>
              <Typography variant='subtitle1' fontWeight={800}>Pedido actual</Typography>
              <Typography variant='caption' color='text.secondary'>Artículos ya registrados para {name}</Typography>
            </Box>
            <Chip size='small' color='info' label={`Total: $${ccyFormat(existingTotal)}`} />
          </Box>
          <Stack spacing={0.65}>
            {existingItems.map((item, index) => {
              const lineTotal = (item.prices_sold?.sale_price || 0) * (item.quantity_sold || 0);
              const variant = [item.color_selected, item.size_selected].filter(Boolean).join(' / ');
              return (
                <Box
                  key={`${item._id || item.article_id || item.sku}-${index}`}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  gap={1}
                  sx={{ px: 1, py: 0.65, bgcolor: 'background.paper', borderRadius: 1 }}
                >
                  <Box minWidth={0}>
                    <Typography variant='body2' fontWeight={700} noWrap>{item.name}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {item.quantity_sold} x ${ccyFormat(item.prices_sold?.sale_price || 0)}{variant ? ` · ${variant}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant='body2' fontWeight={800} sx={{ flexShrink: 0 }}>
                    ${ccyFormat(lineTotal)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='body2' color='text.secondary'>Total con artículos nuevos</Typography>
            <Typography variant='subtitle1' fontWeight={900} color='info.dark'>
              ${ccyFormat(existingTotal + totalSale)}
            </Typography>
          </Box>
        </Box>
      )}

      {isMobile && (
        <Box mb={1.5}>
          <ToggleButtonGroup
            exclusive
            value={mobileSection}
            onChange={(_, next) => next && setMobileSection(next)}
            fullWidth
            size="small"
          >
            <ToggleButton value="products">Productos</ToggleButton>
            <ToggleButton value="summary">Resumen</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={2}
        alignItems="flex-start"
        sx={{
          flex: isMobile ? 'unset' : 1,
          minHeight: 0
        }}
      >
        {/* Columna izquierda: productos */}
        {(!isMobile || mobileSection === 'products') && <Box
          sx={{
            flex: 1,
            minHeight: 0,
            height: isMobile ? 'calc(100dvh - 260px)' : '100%',
            maxHeight: isMobile ? 'calc(100dvh - 260px)' : '100%',
            overflowY: 'auto',
            pr: isMobile ? 0 : 1,
            width: '100%'
          }}
        >
          <Stack spacing={1.1}>
            {data.map((item) => {
              const { name, image, prices_sold, quantity_sold, sku, discount_applied, original_price } = item;
              const hasDiscount = discount_applied && original_price;
              const lineTotal = (prices_sold?.sale_price || 0) * (quantity_sold || 0);

              return (
                <Box
                  key={`${item.article_id}-${sku}`}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    p: 1.1,
                    boxShadow: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: alpha(theme.palette.primary.main, 0.45)
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '40px 1.3fr 0.8fr 0.7fr auto' },
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '36px 1fr auto',
                        gap: 1,
                        alignItems: 'center',
                        gridColumn: { xs: '1 / -1', sm: '1 / 3' }
                      }}
                    >
                      <Box
                        component='img'
                        src={image || BRANDING.logoUrl}
                        alt={name}
                        onClick={() => setImagePreview({ open: true, src: image || BRANDING.logoUrl, name })}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: 1.5,
                          objectFit: 'cover',
                          border: `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer'
                        }}
                      />

                      <Box minWidth={0}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            lineHeight: 1.2,
                            fontSize: { xs: '0.86rem', sm: '0.95rem' },
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationColor: alpha(theme.palette.primary.main, 0.35)
                          }}
                          onClick={() => setImagePreview({ open: true, src: image || BRANDING.logoUrl, name })}
                        >
                          {name}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' noWrap>{sku}</Typography>
                        {hasDiscount && (
                          <Chip
                            icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                            label={`${discount_applied.offer_name} ${discount_applied.discount_percentage ? `-${discount_applied.discount_percentage}%` : `-$${discount_applied.discount_amount}`}`}
                            size='small'
                            sx={{ mt: 0.5, height: 20, fontSize: '0.68rem', maxWidth: '100%' }}
                            color='error'
                          />
                        )}
                      </Box>

                      <IconButton size='small' color='error' onClick={() => onRemoveItem && onRemoveItem(item)}>
                        <Delete fontSize='small' />
                      </IconButton>
                    </Box>

                    <TextField
                      label='Cant.'
                      type='number'
                      size='small'
                      value={quantity_sold}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10) || 0;
                        onChangePrice && onChangePrice(CHANGE.QUANTITY, value, item);
                      }}
                      inputProps={{ min: 1 }}
                      sx={{ minWidth: 84, gridColumn: { xs: '1 / 2', sm: '3 / 4' } }}
                    />

                    <TextField
                      label='Precio'
                      type='number'
                      size='small'
                      value={prices_sold.sale_price}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        onChangePrice && onChangePrice(CHANGE.PRICE, value, item);
                      }}
                      InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
                      sx={{ minWidth: 108, gridColumn: { xs: '1 / 2', sm: '4 / 5' } }}
                    />

                    <Box
                      display='flex'
                      alignItems='center'
                      gap={0.75}
                      justifyContent='flex-end'
                      sx={{ gridColumn: { xs: '1 / 2', sm: '5 / 6' } }}
                    >
                      <Chip size='small' color='success' label={`$${ccyFormat(lineTotal)}`} sx={{ fontWeight: 700 }} />
                    </Box>
                  </Box>

                  {item.type_detail && item.type_detail.length > 0 && (() => {
                    const availableVariants = item.type_detail.filter(v => v.available && v.quantity > 0);
                    if (availableVariants.length === 0) return null;

                    const selectedVariantIndex = selectedVariants[item.article_id];
                    const effectiveColor = selectedVariantIndex !== undefined
                      ? item.type_detail[selectedVariantIndex]?.color
                      : item.color_selected;
                    const selectedVariant = item.type_detail.find(v => v.color === effectiveColor) || null;

                    const effectiveSize = selectedVariants[`${item.article_id}_size`] !== undefined
                      ? selectedVariant?.sizes?.[selectedVariants[`${item.article_id}_size`]]
                      : item.size_selected;

                    return (
                      <Box mt={0.8} display='flex' flexWrap='wrap' alignItems='center' gap={0.6}>
                        {availableVariants.map((variant, idx) => {
                          const originalIndex = item.type_detail.findIndex(v =>
                            v.color === variant.color && JSON.stringify(v.sizes) === JSON.stringify(variant.sizes)
                          );
                          const isSelected = effectiveColor === variant.color;
                          return (
                            <Chip
                              key={idx}
                              label={variant.color}
                              size='small'
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => {
                                setSelectedVariants(prev => ({ ...prev, [item.article_id]: originalIndex, [`${item.article_id}_size`]: undefined }));
                                onChangePrice && onChangePrice(CHANGE.VARIANT_COLOR, variant.color, item);
                              }}
                              sx={{ height: 22, fontSize: '0.72rem', cursor: 'pointer' }}
                            />
                          );
                        })}

                        {selectedVariant?.sizes?.length > 0 && (
                          <>
                            <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.3 }} />
                            {selectedVariant.sizes.map((size, sizeIdx) => {
                              const isSizeSelected = effectiveSize === size;
                              const sizeDetail = selectedVariant.size_details?.find(sd => sd.size === size);
                              const outOfStock = sizeDetail ? (sizeDetail.quantity === 0 || sizeDetail.available === false) : false;
                              return (
                                <Tooltip
                                  key={sizeIdx}
                                  title={outOfStock ? 'Sin stock' : sizeDetail ? `Stock: ${sizeDetail.quantity}` : ''}
                                  placement='top'
                                >
                                  <span>
                                    <Chip
                                      label={size}
                                      size='small'
                                      color={isSizeSelected ? 'secondary' : 'default'}
                                      variant={isSizeSelected ? 'filled' : 'outlined'}
                                      disabled={outOfStock}
                                      onClick={outOfStock ? undefined : () => {
                                        setSelectedVariants(prev => ({ ...prev, [`${item.article_id}_size`]: sizeIdx }));
                                        onChangePrice && onChangePrice(CHANGE.VARIANT_SIZE, size, item);
                                      }}
                                      sx={{ height: 22, fontSize: '0.72rem', cursor: outOfStock ? 'not-allowed' : 'pointer' }}
                                    />
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </>
                        )}
                      </Box>
                    );
                  })()}

                  {showAdvancedOptions && (
                    <Box mt={1.1}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700, mb: 0.7, display: 'block' }}>
                        Tipo de precio
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={selectedPriceTypes[item.article_id] || null}
                        onChange={(_, nextType) => {
                          if (!nextType) return;
                          const pricesByType = {
                            [TYPE_SALE.MAYOR]: item.prices?.wholesale,
                            [TYPE_SALE.MENOR]: item.prices?.unit,
                            [TYPE_SALE.COSTO]: item.prices?.list_price,
                          };
                          handleChangePrice(nextType, pricesByType[nextType], item);
                        }}
                      >
                        <ToggleButton value={TYPE_SALE.MAYOR} disabled={hasDiscount}>Mayor</ToggleButton>
                        <ToggleButton value={TYPE_SALE.MENOR} disabled={hasDiscount}>Unidad</ToggleButton>
                        <ToggleButton value={TYPE_SALE.COSTO} disabled={hasDiscount}>Costo</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>

          {isMobile && data.length > 0 && (
            <Box mt={1.5}>
              <Button fullWidth variant='contained' onClick={() => setMobileSection('summary')}>
                Ver resumen
              </Button>
            </Box>
          )}
        </Box>}

        {/* Columna derecha: resumen */}
        {(!isMobile || mobileSection === 'summary') && <Box
          sx={{
            width: isMobile ? '100%' : 400,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            height: isMobile ? 'calc(100dvh - 260px)' : '100%',
            maxHeight: isMobile ? 'calc(100dvh - 260px)' : '100%',
            overflow: 'hidden',
            position: isMobile ? 'relative' : 'sticky',
            top: isMobile ? 'auto' : 12,
            alignSelf: 'flex-start'
          }}
        >
          <FormControlLabel
            sx={{ ml: 0.5 }}
            control={
              <Switch
                size="small"
                checked={showAdvancedOptions}
                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Opciones avanzadas
              </Typography>
            }
          />
          <Typography variant="h6" fontWeight={700}>Resumen de venta</Typography>
          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflowY: 'auto', pr: isMobile ? 0 : 0.5 }}>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" label={`Items: ${data.length}`} />
            <Chip size="small" label={`Unidades: ${totalUnits}`} />
            <Chip size="small" color="success" label={`$${ccyFormat(totalSale)}`} />
          </Stack>

          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Detalle rápido</Typography>
            <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
              {data.map((d) => {
                return (
                  <Box
                    key={`${d.article_id}-${d.prices_sold?.sale_price}`}
                    sx={{
                      py: 0.75,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      borderBottom: `1px dashed ${theme.palette.divider}`
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant='body2' sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {`${d.quantity_sold} × ${d.name}`}
                      </Typography>
                    </Box>
                    <Typography variant='body2' fontWeight={700} sx={{ minWidth: 86, textAlign: 'right' }}>
                      {`$${ccyFormat(d.quantity_sold * d.prices_sold.sale_price)}`}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>TOTAL</Typography>
            <Typography variant="h6" fontWeight={800} color="success.main">${ccyFormat(totalSale)}</Typography>
          </Box>

          <Box>
            {/* <Button
              variant='text'
              size='small'
              onClick={() => setShowPaymentDetails((prev) => !prev)}
              endIcon={showPaymentDetails ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: 'none', px: 0 }}
            >
              Pago y vuelto
            </Button> */}

            <Collapse in={showPaymentDetails}>
              <Box display='flex' flexDirection='column' gap={1} mt={0.5}>
                <Box display='flex' gap={1} alignItems='flex-end'>
                  <TextField
                    label="Pago con"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={pago}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setPago(Number.isNaN(value) ? 0 : value)
                    }}
                    sx={{ textAlign: 'flex-end' }}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4 }}>$</span>
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>Vuelto</Typography>
                  <Typography variant="h6" fontWeight={800} color="info.main">
                    ${ccyFormat(changeAmount)}
                  </Typography>
                </Box>

                {pendingAmount > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                    Faltan ${ccyFormat(pendingAmount)} para completar el pago.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>

          <TextField
            label="Nombre pedido"
            variant="standard"
            fullWidth
            value={name}
            inputRef={nameFieldRef}
            onChange={(e) => setName(e.target.value)}
            disabled={Boolean(initialName)}
            helperText={initialName ? 'Nombre bloqueado: estás agregando artículos a este pedido' : undefined}
          />

          <RadioGroup
            options={users}
            onValueChange={(value) => setSelectedUser(value)}
          />

          </Box>

          <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? 'row' : 'column'} spacing={isMobile ? 0.75 : 1}>
              {data.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<LocalOfferIcon fontSize="small" />}
                    disabled={!selectedUser}
                    onClick={() => setOpenModal(true)}
                    sx={isMobile ? { flex: 1, minHeight: 32, fontSize: '0.75rem', px: 0.75, textTransform: 'none', '& .MuiButton-startIcon': { mr: 0.4 } } : { textTransform: 'none' }}
                  >
                    {isMobile ? 'R' : 'Reservar'}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<ShoppingCartCheckoutIcon fontSize="small" />}
                    disabled={!selectedUser}
                    onClick={() =>
                      onSell && onSell({ name, delivery: true, vendor: selectedUser })
                    }
                    sx={isMobile ? { flex: 1, minHeight: 32, fontSize: '0.75rem', px: 0.75, textTransform: 'none', '& .MuiButton-startIcon': { mr: 0.4 } } : { textTransform: 'none' }}
                  >
                    {isMobile ? 'E' : 'Entregar'}
                  </Button>
                </>
              )}
              <Button
                variant={isMobile ? 'outlined' : 'contained'}
                color="error"
                size="small"
                startIcon={<Delete fontSize="small" />}
                onClick={() => onCancel && onCancel()}
                sx={isMobile ? { minHeight: 32, fontSize: '0.75rem', px: 0.75, textTransform: 'none', '& .MuiButton-startIcon': { mr: 0.4 } } : { textTransform: 'none' }}
              >
                {isMobile ? 'C' : 'Cancelar'}
              </Button>
            </Stack>
          </Box>
          {isMobile && (
            <Button sx={{ mt: 1 }} fullWidth variant="outlined" onClick={() => setMobileSection('products')}>
              Volver a productos
            </Button>
          )}
        </Box>}
      </Box>

      <Dialog
        open={imagePreview.open}
        onClose={() => setImagePreview({ open: false, src: '', name: '' })}
        maxWidth='xs'
        fullWidth
      >
        <DialogContent sx={{ p: 1.2 }}>
          <Box
            component='img'
            src={imagePreview.src || BRANDING.logoUrl}
            alt={imagePreview.name || 'Articulo'}
            sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 1.5 }}
          />
          <Typography variant='body2' fontWeight={700} mt={1} textAlign='center'>
            {imagePreview.name}
          </Typography>
        </DialogContent>
      </Dialog>

      <Modal
        open={openModal}
        Content={() =>
          <Deposit
            maxQuantity={totalSale}
            onClose={() => setOpenModal(false)}
            onConfirm={(quantity) =>
              onSell && onSell({ name, delivery: false, vendor: selectedUser, deposit: quantity })
            }
          />
        }
      />
    </Box>

  );
};

export default CarritoView;
